import { useSelector } from 'react-redux'
import { countContentByType } from './lib/arrayOperations'
import { findFirstIncomplete } from './components/Training/helpers'
import { setActiveContent } from './redux/trainingProgram/actions'
import { getHighestPriorityResult } from './utils/trainingOccupancySyncUtils'

function useActiveContent() {
    const playlist = useSelector(state => state.trainingProgram.playlist)
    const sectionIndex = useSelector(state => state.trainingProgram.sectionIndex)
    const contentIndex = useSelector(state => state.trainingProgram.contentIndex)
    if (!playlist.sections || sectionIndex === null || contentIndex === null) return null
    return playlist.sections[sectionIndex].content[contentIndex]
}

function useCurrentProgress() {
    const result = useCurrentTrainingResult()
    const playlist = useSelector(state => state.trainingProgram.playlist)
    if (!result || !playlist) return 0
    const quizTotal = countContentByType(playlist, 'QUIZ')
    const videoTotal = countContentByType(playlist, 'VIDEO')
    const max = [quizTotal, videoTotal].reduce((acc, val) => acc + val, 0)
    const progress
        = [result.quizzesCompleted, result.videosCompleted]
            .reduce((acc, val) => acc + (val !== null ? val.length : 0), 0)
    return (progress / max) * 100
}

function useCurrentTrainingResult() {
    const playlist = useSelector(state => state.trainingProgram.playlist)
    const trainingResults = useSelector(state => state.app.trainingResults)
    if (!trainingResults || !playlist.pk) return null

    // Find all results for this playlist (may have multiple for users with multiple occupancies)
    const results = trainingResults.filter(result => result.sk?.split('#')[1] === playlist.pk)
    if (!results || results.length === 0) return null

    // Return the highest priority result
    return getHighestPriorityResult(results)
}

function useGenerateHSK() {
    const clientId = useSelector(state => state.app.client.pk)
    const propertyId = useSelector(state => state.app.property.pk)
    const buildingId = useSelector(state => state.app.building.pk)
    const tenantId = useSelector(state => state.app.tenant.pk)
    const userId = useSelector(state => state.app.user.pk)
    return `${clientId}#null#${propertyId}#${buildingId}#${tenantId}#${userId}`
}

function useGenerateHSKWithoutEmail() {
    const clientId = useSelector(state => state.app.client.pk)
    const propertyId = useSelector(state => state.app.property.pk)
    const buildingId = useSelector(state => state.app.building.pk)
    const tenantId = useSelector(state => state.app.tenant.pk)
    return `${clientId}#null#${propertyId}#${buildingId}#${tenantId}`
}

function useGenerateAdminHSKWithoutSuper(contextBuildingId) {
    const clientId = useSelector(state => state.app.client.pk)
    const buildingId = useSelector(state => state.app.building.pk)
    const propertyId = useSelector(state => state.app.property.pk)
    const tenantId = useSelector(state => state.app.tenant.pk)
    const propertyAdmin = useSelector(state => state.app.user?.roles?.includes('PROPERTY_ADMIN'))
    const tenantAdmin = useSelector(state => state.app.user?.roles?.includes('TENANT_ADMIN'))
    const superadmin = useSelector(state => state.app.user?.roles?.includes('SUPER_ADMIN'))

    if (contextBuildingId) {
        return `${clientId}#null#${propertyId}#${contextBuildingId}`
    }

    if (propertyAdmin) {
        return `${clientId}#null#${propertyId}`
    } else if (tenantAdmin) {
        return `${clientId}#null#${propertyId}#${buildingId}#${tenantId}`
    } else {
        return `${clientId}#null#${propertyId}#${buildingId}`
    }
}

function useGenerateAdminHSK(contextBuildingId) {
    const clientId = useSelector(state => state.app.client.pk)
    const buildingId = useSelector(state => state.app.building.pk)
    const propertyId = useSelector(state => state.app.property.pk)
    const tenantId = useSelector(state => state.app.tenant.pk)
    const propertyAdmin = useSelector(state => state.app.user?.roles?.includes('PROPERTY_ADMIN'))
    const tenantAdmin = useSelector(state => state.app.user?.roles?.includes('TENANT_ADMIN'))
    const superadmin = useSelector(state => state.app.user?.roles?.includes('SUPER_ADMIN'))

    if (superadmin) {
        return 'SUPER'
    }

    if (contextBuildingId) {
        return `${clientId}#null#${propertyId}#${contextBuildingId}`
    }

    if (propertyAdmin) {
        return `${clientId}#null#${propertyId}`
    } else if (tenantAdmin) {
        return `${clientId}#null#${propertyId}#${buildingId}#${tenantId}`
    } else {
        return `${clientId}#null#${propertyId}#${buildingId}`
    }
}

function useGeneratePlaylistHSK() {
    const clientId = useSelector(state => state.app.client.pk)
    const buildingId = useSelector(state => state.app.building.pk)
    const propertyId = useSelector(state => state.app.property.pk)
    const tenantId = useSelector(state => state.app.tenant.pk)
    const propertyAdmin = useSelector(state => state.app.user?.roles?.includes('PROPERTY_ADMIN'))
    const tenantAdmin = useSelector(state => state.app.user?.roles?.includes('TENANT_ADMIN'))
    const superadmin = useSelector(state => state.app.user?.roles?.includes('SUPER_ADMIN'))


    if (propertyAdmin) {
        return `${clientId}#null#${propertyId}`
    } else {
        return `${clientId}#null#${propertyId}#${buildingId}`
    }
}

function useGoToFirstIncomplete() {
    const playlist = useSelector(state => state.trainingProgram.playlist)
    const trainingResult = useCurrentTrainingResult()

    return () => {
        const firstIncomplete = findFirstIncomplete(0, playlist, trainingResult)
        if (firstIncomplete) {
            playlist.sections.map((section, sectionIndex) => {
                section.content.map((content, contentIndex) => {
                    if (content.d1 === firstIncomplete.d1 && content.pk === firstIncomplete.pk && content.sk === firstIncomplete.sk) {
                        setActiveContent(sectionIndex, contentIndex)
                    }
                })
            })
        }
    }
}


function usePastProgress() {
    return false
}

function useTrainingButtonTitle(pk) {
    const status = useTrainingStatus(pk)
    if (status === 'PENDING') return 'Start'
    if (status === 'IN_PROGRESS') return 'Resume'
    if (status === 'COMPLETE') return 'Review'
    if (status === 'EXPIRED') return 'Renew'
}

function useTrainingStatus(pk) {
    const trainingResults = useSelector(state => state.app.trainingResults)
    if (trainingResults) {
        // Find all results for this playlist (may have multiple for users with multiple occupancies)
        const results = trainingResults.filter(result => result.sk.split('#')[1] === pk)
        if (results && results.length > 0) {
            // Return the status of the highest priority result
            const highestResult = getHighestPriorityResult(results)
            if (highestResult) return highestResult.sk.split('#')[2]
        }
    }
    return 'PENDING'
}

function useTranslator(translations) {
    let activeTranslation = useSelector(state => state.trainingProgram.activeTranslation)
    if (!activeTranslation) activeTranslation = 'ENGLISH'
    if (!translations) return ''
    const translation = translations.find(translation => translation.language === activeTranslation)
    return translation.text
}

export {
    useActiveContent,
    useCurrentProgress,
    useCurrentTrainingResult,
    useGenerateHSK,
    useGeneratePlaylistHSK,
    useGenerateHSKWithoutEmail,
    useGenerateAdminHSK,
    useGenerateAdminHSKWithoutSuper,
    useGoToFirstIncomplete,
    usePastProgress,
    useTrainingButtonTitle,
    useTrainingStatus,
    useTranslator
}